import React, { useState, useEffect } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, X } from "lucide-react";
import { PROJECT_TYPES, SALE_TYPES, PROJECT_STATUSES } from "@/lib/constants";
import ImageUploadField from "@/components/ImageUploadField";

/**
 * Reusable Edit Project modal.
 * Props: project (initial data), onClose(), onSaved(updated)
 */
export default function EditProjectModal({ project, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: "", project_type: "Konut", sale_type: "Topraktan Satış",
    status: "Ön Satışta", location: "", city: "", district: "", address: "",
    map_link: "", delivery_date: "",
    short_description: "", long_description: "",
    cover_image_url: "", gallery_images: [], video_url: "", brochure_url: "",
    floor_plan_url: "", site_plan_url: "",
    presentation_title: "", presentation_description: "",
    public_inventory_enabled: true, public_show_stock: true, public_show_prices: false,
  });
  const [saving, setSaving] = useState(false);
  const [newGalleryUrl, setNewGalleryUrl] = useState("");

  useEffect(() => {
    if (project) {
      setForm({
        name: project.name || "",
        project_type: project.project_type || "Konut",
        sale_type: project.sale_type || "Topraktan Satış",
        status: project.status || "Ön Satışta",
        location: project.location || "",
        city: project.city || "",
        district: project.district || "",
        address: project.address || "",
        map_link: project.map_link || "",
        delivery_date: project.delivery_date || "",
        short_description: project.short_description || "",
        long_description: project.long_description || "",
        cover_image_url: project.cover_image_url || "",
        gallery_images: Array.isArray(project.gallery_images) ? [...project.gallery_images] : [],
        video_url: project.video_url || "",
        brochure_url: project.brochure_url || "",
        floor_plan_url: project.floor_plan_url || "",
        site_plan_url: project.site_plan_url || "",
        presentation_title: project.presentation_title || "",
        presentation_description: project.presentation_description || "",
        public_inventory_enabled: project.public_inventory_enabled ?? true,
        public_show_stock: project.public_show_stock ?? true,
        public_show_prices: project.public_show_prices ?? false,
      });
    }
  }, [project]);

  const addGalleryUrl = () => {
    const u = (newGalleryUrl || "").trim();
    if (!u) return;
    setForm((f) => ({ ...f, gallery_images: [...f.gallery_images, u] }));
    setNewGalleryUrl("");
  };
  const updateGalleryUrl = (i, val) => {
    setForm((f) => {
      const next = [...f.gallery_images];
      next[i] = val;
      return { ...f, gallery_images: next };
    });
  };
  const removeGalleryUrl = (i) => {
    setForm((f) => ({ ...f, gallery_images: f.gallery_images.filter((_, idx) => idx !== i) }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/projects/${project.id}`, form);
      toast.success("Proje güncellendi");
      // refetch latest
      const { data } = await api.get(`/projects/${project.id}`);
      onSaved?.(data);
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  if (!project) return null;

  return (
    <div className="fixed inset-0 z-50 !mt-0 flex items-start justify-center bg-black/50 p-4 overflow-y-auto"
         onClick={onClose} data-testid="edit-project-modal">
      <div className="bg-white rounded-xl max-w-3xl w-full p-6 my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div className="text-lg font-semibold text-slate-900 font-display">Projeyi Düzenle — {project.name}</div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>

        <form onSubmit={submit} className="space-y-4" data-testid="edit-project-form">
          {/* Temel bilgiler */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <PF label="Proje Adı" required>
              <input required className="input" value={form.name}
                     onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="edit-project-name" />
            </PF>
            <PF label="Durum">
              <select className="input" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="edit-project-status">
                {PROJECT_STATUSES.map((s) => <option key={s}>{s}</option>)}
              </select>
            </PF>
            <PF label="Proje Tipi">
              <select className="input" value={form.project_type} onChange={(e) => setForm({ ...form, project_type: e.target.value })}>
                {PROJECT_TYPES.map((s) => <option key={s}>{s}</option>)}
              </select>
            </PF>
            <PF label="Satış Tipi">
              <select className="input" value={form.sale_type} onChange={(e) => setForm({ ...form, sale_type: e.target.value })}>
                {SALE_TYPES.map((s) => <option key={s}>{s}</option>)}
              </select>
            </PF>
            <PF label="İl"><input className="input" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></PF>
            <PF label="İlçe"><input className="input" value={form.district} onChange={(e) => setForm({ ...form, district: e.target.value })} /></PF>
            <PF label="Lokasyon (kısa)"><input className="input" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></PF>
            <PF label="Teslim Tarihi"><input type="date" className="input" value={form.delivery_date?.slice(0, 10) || ""} onChange={(e) => setForm({ ...form, delivery_date: e.target.value })} /></PF>
            <div className="md:col-span-2">
              <PF label="Açık Adres"><input className="input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></PF>
            </div>
            <div className="md:col-span-2">
              <PF label="Harita Linki (Google Maps)"><input className="input" value={form.map_link} onChange={(e) => setForm({ ...form, map_link: e.target.value })} /></PF>
            </div>
            <div className="md:col-span-2">
              <PF label="Kısa Açıklama"><textarea rows={2} className="input resize-none" value={form.short_description} onChange={(e) => setForm({ ...form, short_description: e.target.value })} /></PF>
            </div>
            <div className="md:col-span-2">
              <PF label="Uzun Açıklama"><textarea rows={4} className="input resize-none" value={form.long_description} onChange={(e) => setForm({ ...form, long_description: e.target.value })} /></PF>
            </div>
          </div>

          {/* Medya alanları */}
          <div className="border-t border-slate-200 pt-4">
            <div className="text-sm font-bold uppercase tracking-wider text-slate-700 mb-3">Medya & Görseller (URL)</div>
            <div className="space-y-3">
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1">Kapak Görseli</div>
                <ImageUploadField value={form.cover_image_url}
                                  onChange={(url) => setForm((current) => ({ ...current, cover_image_url: url }))} />
              </div>

              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1">
                  Galeri Görselleri ({form.gallery_images.length})
                </div>
                <div className="space-y-2" data-testid="gallery-list">
                  {form.gallery_images.map((url, i) => (
                    <div key={i} className="flex items-center gap-2" data-testid={`gallery-row-${i}`}>
                      <input className="input flex-1" value={url} onChange={(e) => updateGalleryUrl(i, e.target.value)} />
                      <button type="button" onClick={() => removeGalleryUrl(i)} className="text-red-500 hover:text-red-700 p-1.5" data-testid={`gallery-remove-${i}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  <div className="flex items-center gap-2">
                    <input className="input flex-1" placeholder="Yeni görsel URL'i…"
                           value={newGalleryUrl} onChange={(e) => setNewGalleryUrl(e.target.value)}
                           data-testid="gallery-new-input" />
                    <button type="button" onClick={addGalleryUrl}
                            className="px-3 py-2 text-sm bg-slate-900 text-white rounded-lg inline-flex items-center gap-1"
                            data-testid="gallery-add-btn">
                      <Plus className="w-4 h-4" /> Ekle
                    </button>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <PF label="Video URL"><input className="input" value={form.video_url} onChange={(e) => setForm({ ...form, video_url: e.target.value })} placeholder="https://… veya YouTube embed URL" /></PF>
                <PF label="Broşür PDF URL"><input className="input" value={form.brochure_url} onChange={(e) => setForm({ ...form, brochure_url: e.target.value })} data-testid="edit-brochure-url" /></PF>
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1">Genel Kat Planı</div>
                  <ImageUploadField value={form.floor_plan_url}
                                    onChange={(url) => setForm((current) => ({ ...current, floor_plan_url: url }))} compact />
                </div>
                <div className="md:col-span-2">
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1">Vaziyet Planı</div>
                  <ImageUploadField value={form.site_plan_url}
                                    onChange={(url) => setForm((current) => ({ ...current, site_plan_url: url }))} compact />
                </div>
              </div>
            </div>
          </div>

          {/* Sunum metinleri */}
          <div className="border-t border-slate-200 pt-4">
            <div className="text-sm font-bold uppercase tracking-wider text-slate-700 mb-3">Sunum Ekranı Metinleri</div>
            <div className="grid grid-cols-1 gap-3">
              <PF label="Sunum Başlığı"><input className="input" value={form.presentation_title}
                                                onChange={(e) => setForm({ ...form, presentation_title: e.target.value })}
                                                placeholder="Geleceğin yaşam alanları" /></PF>
              <PF label="Sunum Açıklaması"><textarea rows={2} className="input resize-none" value={form.presentation_description}
                                                      onChange={(e) => setForm({ ...form, presentation_description: e.target.value })} /></PF>
            </div>
          </div>

          <div className="border-t border-slate-200 pt-4 space-y-2">
            <div className="text-sm font-bold uppercase tracking-wider text-slate-700">Müşteriye Açık Bilgiler</div>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={form.public_inventory_enabled}
                     onChange={(e) => setForm({ ...form, public_inventory_enabled: e.target.checked })} />
              Proje envanterini göster
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={form.public_show_stock} disabled={!form.public_inventory_enabled}
                     onChange={(e) => setForm({ ...form, public_show_stock: e.target.checked })} />
              Bölüm numaralarını ve stok durumunu göster
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={form.public_show_prices}
                     onChange={(e) => setForm({ ...form, public_show_prices: e.target.checked })} />
              Fiyatları göster
            </label>
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-slate-200 rounded-lg">Vazgeç</button>
            <button type="submit" disabled={saving}
                    className="px-4 py-2 text-sm bg-slate-900 hover:bg-slate-800 text-white rounded-lg disabled:opacity-60"
                    data-testid="submit-edit-project">
              {saving ? "Kaydediliyor…" : "Değişiklikleri Kaydet"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function PF({ label, required, children }) {
  return (
    <label className="block">
      <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1">{label}{required && <span className="text-red-500"> *</span>}</div>
      {children}
    </label>
  );
}
